import { useState, useEffect } from 'react';

/**
 * PhoneListDrawer — Manage phone numbers for text/voice careflow outputs.
 *
 * Props:
 *   open      — drawer visibility
 *   onClose   — close callback
 *   onSave    — save callback (receives { escalation_child_id, reference, event_time, send_next_message, send_max, campus_id })
 *   saving    — boolean, disables buttons while saving
 *   target    — { row, outputType } where outputType is 'textmessages' or 'voicemessages'
 *   campusId  — current campus ID
 *   canEditTiming — when false, the Message After input is read-only (low-priv users
 *                   may edit the recipient list but not change the delay).
 *   canToggleOutput — when false the On/Off toggle is hidden entirely. Low-privilege
 *                   users may edit the recipient list of an already-on output but may
 *                   not turn the output off, so they stay bound by the "at least one
 *                   recipient" rule.
 */
const PhoneListDrawer = ({ open, onClose, onSave, saving, target, campusId, canEditTiming = true, canToggleOutput = true }) => {
    const isVoice = target?.outputType === 'voicemessages';
    const title = isVoice ? 'Voice Phone Numbers' : 'Text Phone Numbers';

    const [delay, setDelay] = useState(0);
    const [numbers, setNumbers] = useState([]);
    const [newNumber, setNewNumber] = useState('');
    const [error, setError] = useState('');

    /* Whether the output should be on after saving. Defaults ON every time the drawer
       opens, including for an output that is currently off: reaching this drawer from an
       Off badge has always meant "configure and enable", and defaulting to the stored
       state would break that flow. The toggle exists to turn an output OFF in place,
       which previously required the kebab menu and Edit Careflow. */
    const [outputEnabled, setOutputEnabled] = useState(true);

    // Initialize from target when drawer opens
    useEffect(() => {
        if (open && target) {
            const output = target.row?.outputs?.[target.outputType];
            const refList = output?.reference ? output.reference.split(',').filter(Boolean) : [];
            setNumbers(refList);
            setDelay(output?.event_time ? Math.round(output.event_time / 60) : 0);
            setNewNumber('');
            setError('');
            setOutputEnabled(true);
        }
    }, [open, target]);

    /** Strip to digits only */
    const stripToDigits = (val) => val.replace(/[^0-9]/g, '');

    /** Allow digits, dashes, spaces in display */
    const isValidInput = (val) => /^[0-9\- ]*$/.test(val);

    const handleAddNumber = () => {
        const trimmed = newNumber.trim();
        if (!trimmed) return;

        const digits = stripToDigits(trimmed);
        if (digits.length !== 10) {
            setError('Phone number must be 10 digits.');
            return;
        }

        if (numbers.includes(digits)) {
            setError('This number is already in the list.');
            return;
        }

        setNumbers((prev) => [...prev, digits]);
        setNewNumber('');
        setError('');
    };

    const handleRemoveNumber = (index) => {
        setNumbers((prev) => prev.filter((_, i) => i !== index));
    };

    const handleNewNumberChange = (e) => {
        const val = e.target.value;
        if (isValidInput(val)) {
            setNewNumber(val);
            if (error) setError('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddNumber();
        }
    };

    const handleSave = () => {
        const output = target.row?.outputs?.[target.outputType];
        onSave({
            escalation_child_id: output?.escalation_child_id || 0,
            /* Reported as intent only — WellnessSettingsPage decides what turning an
               output off clears, so all three output drawers behave identically. */
            enabled: outputEnabled,
            reference: numbers,
            event_time: parseInt(delay) * 60,
            send_next_message: 0,
            send_max: false,
            campus_id: campusId,
        });
    };

    /** Format a digit string for display (e.g. 5551234567 -> 555-123-4567) */
    const formatPhone = (digits) => {
        if (digits.length === 10) {
            return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        return digits;
    };

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer${open ? ' is-open' : ''}`}>
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">{title}</h2>
                    <button className="dp-drawer__close" onClick={onClose}>
                        <i className="fe fe-x" />
                    </button>
                </div>

                <div className="dp-drawer__body">
                    {target?.row ? (
                        <p style={{ marginBottom: 'var(--space-3, 0.75rem)', color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                            Careflow: <strong>{target.row.careflow_description || '(unnamed)'}</strong>
                        </p>
                    ) : null}

                    {/* On/Off for the whole output. Turning it off here is the point of this
                        control: previously the only route was the row kebab -> Edit Careflow,
                        which customers were not finding. Hidden for users who may not toggle
                        outputs, so they remain bound by the one-recipient rule. */}
                    {canToggleOutput ? (
                        <div className="dp-drawer__field">
                            <div className="ws-toggle-row">
                                <span className="ws-toggle-label">Output Enabled</span>
                                <label className="dp-toggle">
                                    <input
                                        type="checkbox"
                                        checked={outputEnabled}
                                        onChange={(e) => setOutputEnabled(e.target.checked)}
                                    />
                                    <span className="dp-toggle__track" />
                                </label>
                            </div>
                            {!outputEnabled ? (
                                <p className="dp-drawer__error">
                                    Saving will turn this output off and remove all phone numbers. Timing settings are kept.
                                </p>
                            ) : null}
                        </div>
                    ) : null}

                    {/* Message After */}
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">Message After (minutes)</label>
                        <input
                            className="dp-drawer__input"
                            type="number"
                            min="0"
                            max="120"
                            value={delay}
                            onChange={(e) => setDelay(e.target.value)}
                            disabled={!canEditTiming}
                            readOnly={!canEditTiming}
                            title={!canEditTiming ? 'You do not have permission to change this value.' : undefined}
                        />
                        <p style={{ marginTop: 'var(--space-1, 0.25rem)', fontSize: 'var(--font-size-xs, 0.75rem)', color: 'var(--color-text-muted, #6b7280)' }}>
                            0 = Immediate Send. 1–120 = Minutes from alarm generation when notification will send.
                        </p>
                    </div>

                    {/* Current Phone Numbers */}
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            Phone Numbers ({numbers.length})
                        </label>
                        <div
                            style={{
                                maxHeight: '300px',
                                overflowY: 'auto',
                                border: '1px solid var(--color-border, #d1d5db)',
                                borderRadius: 'var(--radius-md, 0.375rem)',
                            }}
                        >
                            {numbers.length === 0 ? (
                                <div
                                    style={{
                                        padding: 'var(--space-3, 0.75rem)',
                                        textAlign: 'center',
                                        color: 'var(--color-text-muted, #6b7280)',
                                        fontSize: 'var(--font-size-sm, 0.875rem)',
                                    }}
                                >
                                    No phone numbers added.
                                </div>
                            ) : (
                                numbers.map((num, idx) => (
                                    <div
                                        key={idx}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: 'var(--space-2, 0.5rem) var(--space-3, 0.75rem)',
                                            borderBottom: idx < numbers.length - 1 ? '1px solid var(--color-border, #e5e7eb)' : 'none',
                                        }}
                                    >
                                        <span style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                                            {formatPhone(num)}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveNumber(idx)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: 'var(--color-text-muted, #6b7280)',
                                                padding: '2px',
                                                lineHeight: 1,
                                            }}
                                            title="Remove number"
                                        >
                                            <i className="fe fe-x" />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Add Phone Number */}
                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">Add Phone Number</label>
                        <div style={{ display: 'flex', gap: 'var(--space-2, 0.5rem)' }}>
                            <input
                                className={`dp-drawer__input${error ? ' is-invalid' : ''}`}
                                type="text"
                                placeholder="555-123-4567"
                                value={newNumber}
                                onChange={handleNewNumberChange}
                                onKeyDown={handleKeyDown}
                                style={{ flex: 1 }}
                            />
                            <button
                                type="button"
                                className="dp-btn dp-btn--primary dp-btn--sm"
                                onClick={handleAddNumber}
                            >
                                Add
                            </button>
                        </div>
                        {error ? <p className="dp-drawer__error">{error}</p> : null}
                    </div>

                    {outputEnabled && numbers.length === 0 ? (
                        <p className="dp-drawer__error">
                            At least one phone number is required to keep this output on. Add one above, or switch Output Enabled off.
                        </p>
                    ) : null}
                </div>

                <div className="dp-drawer__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                    <button
                        className="dp-btn dp-btn--primary"
                        disabled={saving || (outputEnabled && numbers.length === 0)}
                        title={outputEnabled && numbers.length === 0 ? 'Add at least one phone number to enable this output' : undefined}
                        onClick={handleSave}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default PhoneListDrawer;
